import { motion } from 'framer-motion';
import { Star, Film } from 'lucide-react';
import type { RatedMovie } from '../../../services/traktApi';
import { LABELS } from '../../../data/labels';

export const TopRatedMoviesCard = ({ movies, loading, labels }: {
  movies: RatedMovie[];
  loading: boolean;
  labels: typeof LABELS['fr'];
}) => {
  if (loading) {
    return (
      <div className="card card-hover p-6 h-full">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="flex gap-3 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[2/3] w-32 flex-shrink-0 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="card card-hover p-6 h-full"
    >
      <div className="section-title mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-[#ed1c24]" />
          {labels.top_movies}
        </div>
        <span className="text-xs text-[#8e8e93]">Faites défiler →</span>
      </div>

      {/* Container défilable horizontal */}
      <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-thin scrollbar-thumb-[#ed1c24]/30 scrollbar-track-transparent">
        <div className="flex gap-4" style={{ width: 'max-content' }}>
          {movies.map((item, idx) => {
            // Construire l'URL de l'affiche à partir du poster ou du tmdb_id
            const posterUrl = item.movie.poster && item.movie.poster.startsWith('http')
              ? item.movie.poster
              : item.movie.ids.tmdb
                ? `https://image.tmdb.org/t/p/w342/${item.movie.ids.tmdb}.jpg`
                : null;

            return (
              <motion.div
                key={item.movie.ids.trakt}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(idx * 0.03, 0.5) }}
                className="group relative flex-shrink-0 w-32"
              >
                <a
                  href={`https://trakt.tv/movies/${item.movie.ids.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1a1a1a] shadow-md">
                    {posterUrl ? (
                      <img
                        src={posterUrl}
                        alt={item.movie.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          // Fallback si l'image ne charge pas
                          (e.target as HTMLImageElement).style.display = 'none';
                          const parent = (e.target as HTMLImageElement).parentElement;
                          if (parent) {
                            parent.innerHTML = `<div class="w-full h-full flex flex-col items-center justify-center text-gray-500 p-2"><svg class="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"></path></svg><span class="text-xs text-center line-clamp-2">${item.movie.title}</span></div>`;
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[#666] p-2">
                        <Film className="w-8 h-8 mb-1" />
                        <span className="text-xs text-center line-clamp-2">{item.movie.title}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <div className="text-xs font-medium text-[#1d1d1f] truncate" title={item.movie.title}>
                      {item.movie.title}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#8e8e93]">{item.movie.year}</span>
                      <span className="text-xs font-bold text-[#ed1c24]">★ {item.rating}</span>
                    </div>
                  </div>
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};
